import { ResizeObserver as Polyfill } from "@juggle/resize-observer";
import { Box, Typography } from "@mui/material";
import type { SignatureBiometrics } from "@ssf/sign-worker";
import type { ReactNode } from "react";
import { useState } from "react";
import CanvasDraw from "react-canvas-draw";
import { useTranslation } from "react-i18next";
import useResizeObserver from "use-resize-observer";
import { useSignatureBiometrics } from "./useSignatureBiometrics.js";

const height = 256;

window.ResizeObserver = window.ResizeObserver || Polyfill;

export const useDrawSignature = () => {
  const [canvas, setCanvas] = useState<CanvasDraw>();
  return { canvas, setCanvas };
};

export const DrawSignature = ({
  canvas,
  setCanvas,
  onChange,
  children,
}: ReturnType<typeof useDrawSignature> & {
  onChange: (blob: Blob, biometrics: SignatureBiometrics) => void;
  children?: ReactNode;
}) => {
  const { t } = useTranslation("DrawSignature");
  const { ref, width = 400 } = useResizeObserver();
  const { pointerHandlers, computeBiometrics } = useSignatureBiometrics(
    width,
    height,
  );

  return (
    <Box ref={ref} sx={{ position: "relative" }} {...pointerHandlers}>
      {children}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          px: 2,
          minWidth: "70%",
          minHeight: ({ spacing }) => spacing(6 + 2),
          pt: 2 + 2,
          borderBottom: "1px solid currentColor",
          textAlign: "center",
        }}
      >
        <Typography
          variant="h3"
          sx={[
            {
              transition: "opacity ease-in-out 200ms",
              ".MuiPaper-root:hover &": { opacity: 0.1 },
            },
            canvas ? { opacity: 0.1 } : {},
          ]}
        >
          {t("drawHere")}
        </Typography>
      </Box>
      <CanvasDraw
        canvasWidth={width}
        canvasHeight={height}
        hideGrid
        brushRadius={1}
        lazyRadius={1}
        brushColor="#111"
        style={{ background: "none" }}
        onChange={(canvasObj) => {
          setCanvas(canvasObj);
          (
            canvasObj as CanvasDraw & {
              canvas: { drawing: HTMLCanvasElement };
            }
          ).canvas.drawing.toBlob((file) => {
            if (file) {
              onChange(file, computeBiometrics());
            }
          });
        }}
      />
    </Box>
  );
};
